import { helper } from '@ember/component/helper';
import { htmlSafe } from '@ember/template';

const DANGEROUS_TAGS = new Set(['script', 'iframe', 'object', 'embed', 'base', 'form', 'meta', 'link']);
const DANGEROUS_ATTR = /^on/i;
// Script-executing URL schemes. Strip ALL C0 control chars + whitespace before
// testing: the DOM decodes entities in attribute values, so `java&#x09;script:`
// arrives as `java\tscript:` and browsers ignore the tab when resolving the
// scheme. A leading-`\s*` regex alone would miss those obfuscated forms.
const DANGEROUS_SCHEME = /^(?:javascript|vbscript):/i;
// Includes svg xlink:href, which also resolves URLs and bypassed the plain
// `href`/`src` set.
const URL_ATTRS = new Set(['href', 'src', 'action', 'formaction', 'data', 'xlink:href']);
function hasDangerousUrl(value) {
  const normalized = String(value).replace(/[\u0000-\u0020]+/g, '');
  return DANGEROUS_SCHEME.test(normalized);
}
const ESCAPE_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};
function sanitizeNode(node) {
  const children = [...node.childNodes];
  if (node.nodeType !== Node.ELEMENT_NODE) {
    children.forEach(sanitizeNode);
    return;
  }
  if (DANGEROUS_TAGS.has(node.tagName.toLowerCase())) {
    node.remove();
    return;
  }
  [...node.attributes].forEach(attr => {
    if (DANGEROUS_ATTR.test(attr.name)) {
      node.removeAttribute(attr.name);
    } else if (URL_ATTRS.has(attr.name) && hasDangerousUrl(attr.value)) {
      node.removeAttribute(attr.name);
    }
  });
  children.forEach(sanitizeNode);
}

/**
 * Sanitize an HTML string (plain-string variant).
 * Strips dangerous tags (script, iframe, object, embed, base, form, meta,
 * link), on* event-handler attributes and javascript: URLs.
 * Returns a plain string.
 */
function sanitizeHtmlString(html) {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    sanitizeNode(doc.body);
    return doc.body.innerHTML;
  } catch {
    return '';
  }
}

// Allowlist for short tenant texts that only need links and inline formatting (consent / privacy labels): every
// other element is unwrapped (its text stays), every other attribute dropped.
const INLINE_TAGS = new Set(['a', 'b', 'strong', 'i', 'em', 'u', 's', 'small', 'sub', 'sup', 'br', 'span', 'p', 'ul', 'ol', 'li']);
const SAFE_HREF = /^(?:https?:|mailto:|tel:|#|\/)/i;
function inlineNode(node, doc) {
  [...node.childNodes].forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) return;
    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove(); // comments, processing instructions
      return;
    }
    const tag = child.tagName.toLowerCase();
    if (DANGEROUS_TAGS.has(tag) || tag === 'style' || tag === 'svg' || tag === 'math') {
      child.remove();
      return;
    }
    inlineNode(child);
    if (!INLINE_TAGS.has(tag)) {
      child.replaceWith(...child.childNodes);
      return;
    }
    const href = tag === 'a' ? child.getAttribute('href') : null;
    const blank = tag === 'a' && child.getAttribute('target') === '_blank';
    [...child.attributes].forEach(attr => child.removeAttribute(attr.name));
    if (tag === 'a') {
      const clean = href ? String(href).replace(/[\u0000-\u0020]+/g, '') : '';
      if (clean && SAFE_HREF.test(clean)) child.setAttribute('href', href.trim());
      if (blank) child.setAttribute('target', '_blank');
      child.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

/**
 * Allowlist sanitizer (plain-string variant) for short tenant texts: keeps only links (http/https/mailto/tel,
 * relative or #anchors; target=_blank kept, rel forced to noopener noreferrer) and inline formatting
 * (INLINE_TAGS). Anything else is unwrapped to its text or removed (script/style/iframe/svg…).
 */
function sanitizeInlineHtmlString(html) {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    inlineNode(doc.body, doc);
    return doc.body.innerHTML;
  } catch {
    return '';
  }
}

/** sanitizeInlineHtmlString as an htmlSafe SafeString (helper `safe-inline-html`). */
function sanitizeInlineHtml(html) {
  return htmlSafe(sanitizeInlineHtmlString(html));
}

/**
 * Sanitize an HTML string and return it as an htmlSafe SafeString,
 * ready to render with {{...}} in templates or assign to tracked state.
 */
function sanitizeHtml(html) {
  return htmlSafe(sanitizeHtmlString(html));
}

/**
 * Escape plain text for safe interpolation into HTML.
 * Replaces & < > " ' with their entity equivalents. Returns a plain string.
 */
function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str).replace(/[&<>"']/g, ch => ESCAPE_MAP[ch]);
}
var safeString = helper(([string]) => sanitizeHtml(string));

export { safeString as default, escapeHtml, sanitizeHtml, sanitizeHtmlString, sanitizeInlineHtml, sanitizeInlineHtmlString };
//# sourceMappingURL=safe-string.js.map
